export interface ColumnDefinition {
  key: string;
  label: string;
  locked?: boolean;
  defaultHidden?: boolean;
}

export interface ColumnLayout {
  order: readonly string[];
  visible: Readonly<Record<string, boolean>>;
}

const visibleByDefault = (column: ColumnDefinition): boolean =>
  column.locked === true || column.defaultHidden !== true;

export const defaultLayout = (columns: readonly ColumnDefinition[]): ColumnLayout => ({
  order: columns.map((column) => column.key),
  visible: Object.fromEntries(columns.map((column) => [column.key, visibleByDefault(column)])),
});

export const visibleKeys = (layout: ColumnLayout): string[] =>
  layout.order.filter((key) => layout.visible[key]);

export const hiddenCount = (layout: ColumnLayout): number =>
  layout.order.filter((key) => !layout.visible[key]).length;

const lockedKeys = (columns: readonly ColumnDefinition[]): Set<string> =>
  new Set(columns.filter((column) => column.locked).map((column) => column.key));

export const withVisibility = (
  layout: ColumnLayout,
  key: string,
  isVisible: boolean,
  columns: readonly ColumnDefinition[],
): ColumnLayout =>
  lockedKeys(columns).has(key)
    ? layout
    : { ...layout, visible: { ...layout.visible, [key]: isVisible } };

export const withAllVisible = (layout: ColumnLayout): ColumnLayout => ({
  ...layout,
  visible: Object.fromEntries(layout.order.map((key) => [key, true])),
});

export const withAllHidden = (
  layout: ColumnLayout,
  columns: readonly ColumnDefinition[],
): ColumnLayout => {
  const locked = lockedKeys(columns);
  return {
    ...layout,
    visible: Object.fromEntries(layout.order.map((key) => [key, locked.has(key)])),
  };
};

export const withColumnsMoved = (
  layout: ColumnLayout,
  keys: readonly string[],
  beforeKey: string | null,
  columns: readonly ColumnDefinition[],
): ColumnLayout => {
  const locked = lockedKeys(columns);
  const moved = layout.order.filter((key) => keys.includes(key) && !locked.has(key));
  if (
    moved.length === 0 ||
    (beforeKey !== null && (locked.has(beforeKey) || moved.includes(beforeKey)))
  ) {
    return layout;
  }

  const without = layout.order.filter((candidate) => !moved.includes(candidate));
  const at = beforeKey === null ? without.length : without.indexOf(beforeKey);
  if (at === -1) return layout;

  return { ...layout, order: [...without.slice(0, at), ...moved, ...without.slice(at)] };
};

export const withMoved = (
  layout: ColumnLayout,
  key: string,
  beforeKey: string | null,
  columns: readonly ColumnDefinition[],
): ColumnLayout => withColumnsMoved(layout, [key], beforeKey, columns);

export const reconcile = (
  stored: ColumnLayout,
  columns: readonly ColumnDefinition[],
): ColumnLayout => {
  const declared = new Set(columns.map((column) => column.key));
  const locked = lockedKeys(columns);
  const kept = [...new Set(stored.order)].filter((key) => declared.has(key));
  const added = columns.map((column) => column.key).filter((key) => !kept.includes(key));
  const order = [...kept, ...added];
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const fallback = (key: string): boolean => {
    const column = byKey.get(key);
    return column === undefined ? true : visibleByDefault(column);
  };

  return {
    order,
    visible: Object.fromEntries(
      order.map((key) => [key, locked.has(key) || (stored.visible[key] ?? fallback(key))]),
    ),
  };
};

export const matchesQuery = (column: ColumnDefinition, query: string): boolean =>
  column.label.toLowerCase().includes(query.trim().toLowerCase());
